import { EventEmitter, Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { ModalController, Platform } from '@ionic/angular';
import * as dayjs from 'dayjs';
import {
  modalEnterAnimation,
  modalEnterAnimationVertical,
  modalLeaveAnimation,
  modalLeaveAnimationVertical,
} from '../animation/modal.animation';
import { ChatHeader } from '../models/chat.model';
import { NotificationModel } from '../models/notification.model';
import { ProfileModel } from '../models/profile.model';
import {
  MenuItem,
  MenuItemCatalog,
  MenuItemCatalogPrice,
  RichDataModel,
  TitleAndDescription,
} from '../models/rich-data.model';
import { CartComponent } from '../pages/components/cart/cart.component';
import { CatalogItemDetailPageComponent } from '../pages/components/catalog-item-detail-page/catalog-item-detail-page.component';
import { ChatComponent } from '../pages/components/chat/chat.component';
import { CheckInComponent } from '../pages/components/check-in/check-in.component';
import { CreditCardsComponent } from '../pages/components/credit-cards/credit-cards.component';
import { IframeComponent } from '../pages/components/iframe/iframe.component';
import { LocationControlComponent } from '../pages/components/location-control/location-control.component';
import { MyAccommodationsComponent } from '../pages/components/my-accommodations/my-accommodations.component';
import { MyProfileComponent } from '../pages/components/my-profile/my-profile.component';
import { NewAccommodationComponent } from '../pages/components/new-accommodation/new-accommodation.component';
import { NotificationDetailComponent } from '../pages/components/notification-detail/notification-detail.component';
import { NotificationRequestComponent } from '../pages/components/notification-request/notification-request.component';
import { NotificationsComponent } from '../pages/components/notifications/notifications.component';
import { OrderAlertComponent } from '../pages/components/order-alert/order-alert.component';
import { OrdersComponent } from '../pages/components/orders/orders.component';
import { ProfileComponent } from '../pages/components/profile/profile.component';
import { RequestFormComponent } from '../pages/components/request-form/request-form.component';
import { ReservationComponent } from '../pages/components/reservation/reservation.component';
import { ScheduleComponent } from '../pages/components/schedule/schedule.component';
import { ScheduledCartComponent } from '../pages/components/scheduled-cart/scheduled-cart.component';
import { ShceduledCartNoteComponent } from '../pages/components/shceduled-cart-note/shceduled-cart-note.component';
import { SurveyComponent } from '../pages/components/survey/survey.component';
import { CaptionPipe } from '../pipes/caption.pipe';
import { AnalyticsService } from './analytics.service';
import { DataService } from './data.service';
import { GetTranslationService } from './get-translation.service';
import { LocationService } from './location.service';
import { SurveyService } from './survey.service';
import { VersionCheckerComponent } from '../pages/components/version-checker/version-checker.component';
import { SpaEntranceComponent } from '../pages/restaurant-booking/components/spa-entrance/spa-entrance.component';
import { BonusHistoryComponent } from '../pages/components/bonus-history/bonus-history.component';

@Injectable({
  providedIn: 'root',
})
export class ModalMakerService {
  constructor(
    private mdlCtrl: ModalController,
    private dataServ: DataService,
    private caption: CaptionPipe,
    private locationServ: LocationService,
    private platform: Platform,
    private inappBr: InAppBrowser,
    private analytics: AnalyticsService,
    private translator: GetTranslationService,
    private surveyServ: SurveyService
  ) {
    if (
      !localStorage.getItem('oneSignal') &&
      !localStorage.getItem('notificationDenied')
    ) {
      this.notificationRequest();
    }

    this.welcomeControl();
  }

  async welcomeControl() {
    if (localStorage.getItem('token')) {
      const LASTDATE = localStorage.getItem('lastDate') || null;
      const LASTSCHEDULE = localStorage.getItem('lastSchedule') || null;

      const TODAY = dayjs(new Date()).format('DD');
      const TOMORROW = dayjs(new Date()).add(1, 'day').format('DD');

      if ((LASTDATE && TODAY !== LASTDATE) || !LASTDATE) {
        if (!LASTSCHEDULE || TOMORROW !== LASTSCHEDULE) {
          if (!this.dataServ.rData) {
            await this.dataServ.getRichData();
            await this.dataServ.removeDateRange(this.dataServ.baseData);
          }

          // COMMENT LINE -- BECAUSE OF CRASH ON PHONES
          // LocalNotifications.schedule({
          //   notifications: [
          //     {
          //       title: this.dataServ.rData.name,
          //       body: this.caption.transform(this.dataServ.rData.survey_title),
          //       id: new Date().getTime(),
          //       schedule: {
          //         at: dayjs(new Date()).add(1, 'day').set('hour', 14).set('minute', 0).set('second', 0).set('millisecond', 0).toDate()
          //       },
          //       sound: 'beep.wav'
          //     }
          //   ]
          // });

          localStorage.setItem('lastDate', TODAY);
          localStorage.setItem('lastSchedule', TOMORROW);

          // if (+dayjs(new Date()).format('HH') >= 14) {
          //   this.survey(this.dataServ.rData, 'rich');
          // }
        }
      }
    }
  }

  async openBonusHistory(): Promise<void> {
    const MODAL = await this.mdlCtrl.create({
      component: BonusHistoryComponent,
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  locationMandatoryChecker(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      try {
        // If location check isn't mandatory, we can skip the process
        if (!this.dataServ.menuItem?.location_control_mandatory) {
          resolve(true);
          return;
        }

        // Safari detection (iOS or macOS)
        const isSafari =
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
          this.platform.is('ios') ||
          (this.platform.is('desktop') && /Mac/.test(navigator.platform));

        // Handle notification permission if needed
        let notificationPermit = false;
        if (
          !localStorage.getItem('oneSignal') &&
          this.dataServ.rData?.one_signal_app_id &&
          !sessionStorage.getItem('oneSignal') &&
          !localStorage.getItem('notificationDenied')
        ) {
          notificationPermit = await this.notificationRequest();
        } else if (!this.dataServ.rData?.one_signal_app_id) {
          notificationPermit = true;
        }

        // On Safari/iOS, go directly to location control component for better user experience
        if (isSafari && !this.platform.is('capacitor')) {
          console.log(
            'Safari detected, skipping permission check and showing location modal'
          );
          const locationModalResult = await this.locationControl();
          resolve(!!locationModalResult);
          return;
        }

        // For other browsers/native app
        if (
          notificationPermit ||
          !this.platform.is('capacitor') ||
          !this.dataServ.rData?.one_signal_app_id
        ) {
          try {
            // Check current location permissions
            const permissions = await Geolocation.checkPermissions();

            if (permissions.location !== 'granted') {
              console.log(
                'Location permission not granted, showing location modal'
              );
              const locationModalResult = await this.locationControl();
              resolve(!!locationModalResult);
            } else {
              // We have permission, check if in hotel
              try {
                const isInHotel = await this.locationServ.getLocation();

                if (isInHotel) {
                  console.log('User is in hotel, proceeding');
                  // User is in hotel, proceed
                  resolve(true);
                } else {
                  console.log('User not in hotel, showing location modal');
                  // User isn't in hotel, show location control modal
                  const locationModalResult = await this.locationControl();
                  resolve(!!locationModalResult);
                }
              } catch (error) {
                console.error('Error getting location:', error);
                // On error, show location control modal
                const locationModalResult = await this.locationControl();
                resolve(!!locationModalResult);
              }
            }
          } catch (error) {
            console.error('Error checking permissions:', error);
            // On error, show location control modal
            const locationModalResult = await this.locationControl();
            resolve(!!locationModalResult);
          }
        } else {
          // If notification permission wasn't granted but is required,
          // fall back to location control modal
          const locationModalResult = await this.locationControl();
          resolve(!!locationModalResult);
        }
      } catch (error) {
        console.error('Error in locationMandatoryChecker:', error);
        // On error, we resolve as false to be safe
        resolve(false);
      }
    });
  }

  async openSpaQr(): Promise<void> {
    const MODAL = await this.mdlCtrl.create({
      component: SpaEntranceComponent,
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  async openCatalogItemDetail(
    item: MenuItemCatalog,
    price: MenuItemCatalogPrice
  ): Promise<void> {
    const MODAL = await this.mdlCtrl.create({
      component: CatalogItemDetailPageComponent,
      componentProps: {
        item,
        price,
      },
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_catalog_item_detail`,
      'window',
      'click'
    );

    await MODAL.present();
  }
  async versionControl() {
    const MODAL = await this.mdlCtrl.create({
      component: VersionCheckerComponent,
      cssClass: 'version-checker-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  async showPaymentModal(url: string) {
    const MODAL = await this.mdlCtrl.create({
      component: IframeComponent,
      componentProps: { url },
      cssClass: 'payment-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  async checkin(): Promise<void> {
    const MODAL = await this.mdlCtrl.create({
      component: CheckInComponent,
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_checkin`,
      'window',
      'click'
    );

    await MODAL.present();

    setTimeout(() => {
      this.analytics.setScreenView();
    }, 200);
  }

  async newAccomodation() {
    const MODAL = await this.mdlCtrl.create({
      component: NewAccommodationComponent,
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
    return MODAL;
  }

  async locationControl() {
    // Önce açık bir modal var mı kontrol et ve kapat
    const topModal = await this.mdlCtrl.getTop();
    if (topModal) {
      await topModal.dismiss();
    }

    const MODAL = await this.mdlCtrl.create({
      component: LocationControlComponent,
      cssClass: 'location-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
      swipeToClose: false,
      backdropDismiss: false,
    });

    await MODAL.present();
    const ISINHOTEL = await MODAL.onDidDismiss();
    return ISINHOTEL.data;
  }

  async resButton() {
    if (this.dataServ.rData.online_reservation_url) {
      console.log(this.dataServ.rData.online_reservation_url);

      // this.openLink(
      //   this.caption.transform(this.dataServ.rData.online_reservation_url)
      // );

      window.open(
        this.caption.transform(this.dataServ.rData.online_reservation_url),
        '_blank'
      );
    } else {
      // const MODAL = await this.mdlCtrl.create({
      //   component: RoomReservationComponent,
      //   cssClass: 'detail-modal',
      //   enterAnimation: modalEnterAnimation,
      //   leaveAnimation: modalLeaveAnimation
      // });
      // await MODAL.present();
    }

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_hotel_reservation`,
      'window',
      'click'
    );
  }

  async openLink(url: string) {
    const COLOR = document.documentElement.style.getPropertyValue(
      '--ion-color-primary'
    );
    const CLOSE = await this.translator.getTranslate('Back');

    this.inappBr.create(this.caption.transform(url), '_blank', {
      clearcache: 'yes',
      location: 'yes',
      fullscreen: 'yes',
      enableViewportScale: 'yes',
      toolbar: 'yes',
      closebuttoncolor: '#FFFFFF',
      closebuttoncaption: CLOSE,
      presentationstyle: 'fullscreen',
      transitionstyle: 'crossdissolve',
      toolbartranslucent: 'no',
      toolbarposition: 'top',
      toolbarcolor: COLOR,
      hideurlbar: 'yes',
    });
  }

  async notificationRequest() {
    if (!this.platform.is('capacitor')) {
      return true;
    }

    const MODAL = await this.mdlCtrl.create({
      component: NotificationRequestComponent,
      cssClass: 'notification-req-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
    await MODAL.onDidDismiss();
    return Boolean(localStorage.getItem('oneSignal'));
  }

  async schedule(menuItem: MenuItem) {
    const MODAL = await this.mdlCtrl.create({
      component: ScheduleComponent,
      componentProps: { data: menuItem },
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  async survey(
    surveyData: MenuItem | NotificationModel | RichDataModel,
    type: 'item' | 'notification' | 'rich'
  ) {
    const MODAL = await this.mdlCtrl.create({
      component: SurveyComponent,
      componentProps:
        type === 'item'
          ? { data: surveyData }
          : type === 'notification'
          ? { notificationData: surveyData }
          : { richData: surveyData },
      cssClass: 'survey-modal',
      enterAnimation: modalEnterAnimationVertical,
      leaveAnimation: modalLeaveAnimationVertical,
      swipeToClose: true,
      backdropDismiss: true,
    });

    await MODAL.present();
  }

  async openNotification(notificationData: NotificationModel) {
    const NOTI: number[] = JSON.parse(localStorage.getItem('readedNoti')) || [];

    NOTI.push(notificationData.id);

    localStorage.setItem('readedNoti', JSON.stringify(NOTI));

    const MODAL = await this.mdlCtrl.create({
      component: NotificationDetailComponent,
      componentProps: { notificationData },
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    await MODAL.present();
  }

  async openChat(chat: ChatHeader, live: boolean) {
    const MODAL = await this.mdlCtrl.create({
      component: ChatComponent,
      componentProps: { header: chat, live },
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_${live ? '' : chat.id}_${
        live ? 'live_chat' : 'chat'
      }`,
      'window',
      'click'
    );

    await MODAL.present();

    setTimeout(() => {
      this.analytics.setScreenView();
    }, 200);
  }

  async myProfile(type: string): Promise<void> {
    const MODAL = await this.mdlCtrl.create({
      component: MyProfileComponent,
      componentProps: { type },
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_profile`,
      'window',
      'click'
    );

    await MODAL.present();
  }

  async login(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const loggedIn = new EventEmitter<void>();

      const MODAL = await this.mdlCtrl.create({
        component: ProfileComponent,
        componentProps: {
          loggedIn,
          isModal: true,
        },
        cssClass: 'detail-modal',
        enterAnimation: modalEnterAnimation,
        leaveAnimation: modalLeaveAnimation,
      });

      loggedIn.subscribe(() => {
        MODAL.dismiss();
        resolve(true);
      });

      this.analytics.eventEmitter(
        `${this.dataServ.rData.name}_login`,
        'window',
        'click'
      );

      await MODAL.present();

      MODAL.onDidDismiss().then(() => {
        resolve(false);
      });
    });
  }

  async myAccommodations() {
    const MODAL = await this.mdlCtrl.create({
      component: MyAccommodationsComponent,
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_lodgings`,
      'window',
      'click'
    );

    await MODAL.present();

    setTimeout(() => {
      this.analytics.setScreenView();
    }, 200);
  }

  async myCards() {
    const MODAL = await this.mdlCtrl.create({
      component: CreditCardsComponent,
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_cards`,
      'window',
      'click'
    );

    await MODAL.present();
  }

  async myOrders() {
    const MODAL = await this.mdlCtrl.create({
      component: OrdersComponent,
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_orders`,
      'window',
      'click'
    );

    await MODAL.present();
    setTimeout(() => {
      this.analytics.setScreenView();
    }, 200);
  }

  async notifications() {
    const MODAL = await this.mdlCtrl.create({
      component: NotificationsComponent,
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_notifications`,
      'window',
      'click'
    );

    await MODAL.present();

    setTimeout(() => {
      this.analytics.setScreenView();
    }, 200);
  }

  async reservation(): Promise<void> {
    const ISINHOTEL = await this.locationMandatoryChecker();

    if (ISINHOTEL) {
      const MODAL = await this.mdlCtrl.create({
        component: ReservationComponent,
        componentProps: {
          menuItem: this.dataServ.menuItem,
          menuSection: this.dataServ.menuSection,
          menuCategory: this.dataServ.menuCategory,
        },
        cssClass: 'detail-modal',
        enterAnimation: modalEnterAnimation,
        leaveAnimation: modalLeaveAnimation,
      });

      await MODAL.present();
    }
  }

  async note(timeOfOrder: string, selectedDate: string) {
    const MODAL = await this.mdlCtrl.create({
      component: ShceduledCartNoteComponent,
      componentProps: {
        menuItem: this.dataServ.menuItem,
        menuSection: this.dataServ.menuSection,
        menuCategory: this.dataServ.menuCategory,
        orderTime: timeOfOrder,
        orderDate: selectedDate,
      },
      cssClass: 'detail-modal',
      enterAnimation: modalEnterAnimation,
      leaveAnimation: modalLeaveAnimation,
    });

    this.analytics.eventEmitter(
      `${this.dataServ.rData.name}_cart`,
      'window',
      'click'
    );

    await MODAL.present();
  }

  async cart(): Promise<void> {
    const ISINHOTEL = await this.locationMandatoryChecker();

    if (ISINHOTEL) {
      let MODAL;
      if (this.dataServ.menuItem.allow_scheduled_order) {
        MODAL = await this.mdlCtrl.create({
          component: ScheduledCartComponent,
          componentProps: {
            menuItem: this.dataServ.menuItem,
            menuSection: this.dataServ.menuSection,
            menuCategory: this.dataServ.menuCategory,
          },
          cssClass: 'detail-modal',
          enterAnimation: modalEnterAnimation,
          leaveAnimation: modalLeaveAnimation,
        });
      } else {
        MODAL = await this.mdlCtrl.create({
          component: CartComponent,
          componentProps: {
            menuItem: this.dataServ.menuItem,
            menuSection: this.dataServ.menuSection,
            menuCategory: this.dataServ.menuCategory,
          },
          cssClass: 'detail-modal',
          enterAnimation: modalEnterAnimation,
          leaveAnimation: modalLeaveAnimation,
        });
      }

      this.analytics.eventEmitter(
        `${this.dataServ.rData.name}_cart`,
        'window',
        'click'
      );

      await MODAL.present();
    }
  }

  async orderModal(
    _orderDate: string,
    _orderTime: string,
    _menuItem: MenuItem,
    _profile: ProfileModel
  ) {
    const MODAL = await this.mdlCtrl.create({
      component: OrderAlertComponent,
      componentProps: {
        orderDate: _orderDate,
        orderTime: _orderTime,
        menuItem: _menuItem,
        profile: _profile,
      },
      cssClass: 'detail-modal',
      leaveAnimation: modalLeaveAnimationVertical,
    });

    await MODAL.present();
  }

  async request(service: TitleAndDescription) {
    if (this.dataServ.menuItem.location_control_mandatory) {
      const ISINHOTEL = await this.locationServ.getLocation();

      if (!ISINHOTEL) {
        await this.locationMandatoryChecker();
      }
    } else {
      const MODAL = await this.mdlCtrl.create({
        component: RequestFormComponent,
        componentProps: {
          itemData: this.dataServ.menuItem,
          sectionData: this.dataServ.menuSection,
          serviceData: service,
        },
        enterAnimation: modalEnterAnimation,
        leaveAnimation: modalLeaveAnimation,
      });

      this.analytics.eventEmitter(
        `${this.dataServ.rData.name}_request`,
        'window',
        'click'
      );

      await MODAL.present();
    }
  }
}
